import {
  Box,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react'
import RouterUrl from '@constants/router.constant'
import FooterInfoLayout from '@layouts/footer-info-layout'
import React, { useEffect } from 'react'

export const TermsAndConditionsContent = ({
  title,
  children,
  id
}: {
  title?: string
  children?: React.ReactNode
  id?: string
}) => {
  return (
    <VStack w={'100%'} spacing={1} id={id}>
      {title && (
        <Text textStyle="tcTitle" w={'100%'}>
          {title}
        </Text>
      )}
      {children && (
        <Box textStyle="tcText" w={'100%'}>
          {children}
        </Box>
      )}
    </VStack>
  )
}

const TermsAndConditions = () => {
  const [isMounted, setIsMounted] = React.useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) return null

  return (
    <FooterInfoLayout title="Terms & Conditions">
      <TermsAndConditionsContent>
        JML (HABBIT) is a brand by JML Singapore Pte Ltd. By using the Webstore,
        you agree to the following terms and conditions:
      </TermsAndConditionsContent>

      {/* PAYMENT */}
      <VStack w={'100%'} spacing={0}>
        <Text textStyle="tcTitle" w={'100%'}>
          PAYMENT
        </Text>
        <VStack w={'100%'} spacing={10}>
          <TermsAndConditionsContent title="1.1 Sales Order">
            All Sales Orders submitted to JML Singapore Pte Ltd through the
            Webstore are subject to our acceptance. JML Singapore Pte Ltd
            reserve the right to cancel or terminate the fulfilment of any sales
            order, for any reason whatsoever, even after a sales order is
            confirmed and paid for by the customer, with or without notice, and
            shall not be liable to any party for such termination or
            cancellation. In such an event, we will refund the price of the
            goods purchased to the customer.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent title="1.2 Payment Terms">
            <VStack spacing={8}>
              <Text>
                All prices quoted are inclusive of GST unless otherwise stated.
                Payment for purchases made through the Webstore can only be made
                using digital payments. We do not accept Cash-on-delivery
                payments or personal/company cheques for online purchases.
              </Text>
              <VStack w={'100%'} spacing={0}>
                <Text w={'100%'}>Payment Modes:</Text>
                <OrderedList w={'100%'} pl={10}>
                  <ListItem>Webstore - Credit/Debit Card</ListItem>
                  <ListItem>
                    JML Experience Store - Cash | NETS | Digital Payment
                  </ListItem>
                  <ListItem>
                    Purchasing Hotline - +65 6386 8686 (Mon - Fri, 9 am - 6 pm)
                    - Cash-on-delivery
                  </ListItem>
                </OrderedList>
              </VStack>
            </VStack>
          </TermsAndConditionsContent>
          <TermsAndConditionsContent title="1.3 Payment Processing">
            We accept all major credit card payments applicable via Stripe. JML
            Singapore Pte Ltd is not responsible for errors, acts, and omissions
            of credit card companies.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent title="1.4 Site Errors">
            JML Singapore Pte Ltd reserves the right to correct errors,
            inaccuracies, or omissions, including after a Sales Order has been
            submitted and whether or not the Sales Order has been confirmed and
            your credit card charged. If your credit card has already been
            charged for the purchase and your sales order is cancelled, JML
            Singapore Pte Ltd will issue a credit to your credit card account in
            the amount of the charge. Your individual bank's policies will
            determine the timing of this credit to your account.
          </TermsAndConditionsContent>
        </VStack>
      </VStack>

      {/* DELIVERY */}
      <VStack w={'100%'} spacing={0} id={RouterUrl.ANCHOR.DELIVERY_POLICY}>
        <Text textStyle="tcTitle" w={'100%'}>
          DELIVERY
        </Text>
        <VStack w={'100%'} spacing={8}>
          <TermsAndConditionsContent>
            All products purchased from us will only be delivered to valid
            addresses within Singapore or Malaysia via the respective SG/MY
            domains. We aim to deliver products to you within the stated
            timeframe. If your presence is absent when the parcel is being
            delivered, the courier may leave an absent slip at your doorstep
            with up to 3 redelivery attempts. Please don't hesitate to contact
            our customer services team if you have not received your product
            after the suggested date.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent title="2.1 Delivery Charges">
            All purchases below S$80 for online purchases will bear an
            additional delivery charge. Free delivery does not apply to Malaysia
            orders unless otherwise stated.
          </TermsAndConditionsContent>
        </VStack>
      </VStack>

      {/* CANCELLATIONS, EXCHANGES & RETURNS */}
      <VStack w={'100%'} spacing={0} id={RouterUrl.ANCHOR.EXCHANGES_RETURNS}>
        <Text textStyle="tcTitle" w={'100%'}>
          CANCELLATIONS, EXCHANGES & RETURNS
        </Text>
        <VStack w={'100%'} spacing={10}>
          <TermsAndConditionsContent title="3.1 Sales Cancellation">
            JML Singapore Pte Ltd reserves the right to refund you only on the
            price of any Goods based on your mode of payment, even after the
            relevant sales order is confirmed and payment has been received, in
            lieu of delivery or supply of such Goods within one business day.
            Please call our customer service hotline for assistance.
          </TermsAndConditionsContent>

          <TermsAndConditionsContent title="3.2 Exchanges/Returns">
            <VStack spacing={8}>
              <TermsAndConditionsContent title="For Singapore Orders:">
                You may request an exchange, strictly by appointment, at our
                customer service centre. The products must be returned to JML
                Singapore Pte Ltd before any exchange request will be
                entertained. Please take a look at our exchange policy listed
                below.
              </TermsAndConditionsContent>
              <TermsAndConditionsContent title="For Malaysia Orders:">
                <VStack spacing={10}>
                  <TermsAndConditionsContent>
                    If you require a refund or exchange within the 14 Days Money
                    Back Guarantee period in the event of incomplete, defective,
                    damaged goods, or factory defects, you may request an
                    exchange/refund. Please contact us or email us to initiate
                    the process.
                  </TermsAndConditionsContent>
                  <TermsAndConditionsContent>
                    All original packing materials, manuals, accessories, and
                    gifts with the purchased goods must be returned with the
                    defective goods and proof of purchase or receipt.
                  </TermsAndConditionsContent>
                </VStack>
              </TermsAndConditionsContent>
              <TermsAndConditionsContent title="Our exchange policy does not apply to the following:">
                <VStack spacing={8}>
                  <VStack w={'100%'}>
                    <OrderedList w={'100%'} pl={8}>
                      <ListItem>
                        Hygiene or perishable products (e.g., Innerwear &
                        Cosmetic) unless otherwise stated/advertised
                      </ListItem>
                      <ListItem>
                        Purchases not made directly from any of the JML's
                        platform or distribution partners and without any proof
                        of purchase.
                      </ListItem>
                      <ListItem>
                        Products on clearance (e.g., Campaigns: Lowest Price)
                      </ListItem>
                      <ListItem>Altered or modified products</ListItem>
                      <ListItem>Products with unauthorized repairs</ListItem>
                      <ListItem>Products with misuse/unethical damage</ListItem>
                      <ListItem>Non-electrical products</ListItem>
                      <ListItem>
                        Stains, discolouration, and scratches due to normal wear
                        and tear
                      </ListItem>
                    </OrderedList>
                  </VStack>
                  <TermsAndConditionsContent>
                    All products except those listed above will have a 14-day
                    window (from receiving date) to request a replacement for
                    any damages during transit/defects. For products without a
                    warranty, wear & tear will not be covered after the 14-day
                    window closes.
                  </TermsAndConditionsContent>
                </VStack>
              </TermsAndConditionsContent>
            </VStack>
          </TermsAndConditionsContent>
          <TermsAndConditionsContent title="3.3 Product Liability">
            JML Singapore Pte Ltd will take no liability or claims for any
            damages caused due to the customer's individual negligence.
            Customers should read the user manual before using the product
            correctly
          </TermsAndConditionsContent>
          <TermsAndConditionsContent
            title="REFUNDS"
            id={RouterUrl.ANCHOR.REFUND_POLICY}
          >
            <VStack spacing={8}>
              <TermsAndConditionsContent title="14 Days Money Back Guarantee">
                Refunds are processed strictly based on the value of the product
                price only. Any paid custom fees, duties, taxes, and delivery
                charges will not be refunded.
              </TermsAndConditionsContent>
              <TermsAndConditionsContent title="This policy is strictly not applicable for:">
                <VStack spacing={8}>
                  <VStack w={'100%'}>
                    <OrderedList w={'100%'} pl={8}>
                      <ListItem>
                        Products without any indication of the 14 days money
                        back as advertised
                      </ListItem>
                      <ListItem>
                        Hygiene or perishable products (e.g., Innerwear &
                        Cosmetic) unless otherwise stated/advertised
                      </ListItem>
                      <ListItem>
                        Purchases not made directly from JML's Call centre or
                        JML Webstore
                      </ListItem>
                      <ListItem>
                        Products on clearance (e.g., Campaigns: Lowest Price)
                      </ListItem>
                      <ListItem>
                        JML Experience Center or Retail partners
                      </ListItem>
                      <ListItem>Altered or modified products</ListItem>
                      <ListItem>Products with unauthorized repairs</ListItem>
                      <ListItem>Products with misuse/unethical damage</ListItem>
                      <ListItem>Non-electrical products</ListItem>
                      <ListItem>
                        Stains, discolouration, and scratches due to normal wear
                        and tear
                      </ListItem>
                    </OrderedList>
                  </VStack>
                  <TermsAndConditionsContent title="For Singapore Orders:">
                    Customers must physically visit the JML Service Center for
                    the refund, strictly by appointment. Refund procedures will
                    only be applicable through the purchaser’s mode of payment.
                    The products must be returned to JML Singapore Pte Ltd
                    before any refund request will be entertained. Please refer
                    to our exchange policy for more details.
                  </TermsAndConditionsContent>
                  <TermsAndConditionsContent title="For Malaysia Orders:">
                    <VStack>
                      <TermsAndConditionsContent>
                        If you require a refund within the 14 Days Money Back
                        Guarantee period for products with valid indications,
                        you may request an exchange/refund. All pick-up requests
                        for refunds are payable by you. After making the
                        payment, we will schedule a pickup request with the
                        appointed courier service. The products must be returned
                        to JML Singapore Pte Ltd's appointed warehouse before
                        any new replacements or refunds will be processed.
                        Return the unassembled item in its original packaging,
                        along with all original packing materials, manuals,
                        accessories, and gifts that come with the purchased
                        goods, along with the proof of purchase or receipt for a
                        refund.
                      </TermsAndConditionsContent>
                      <TermsAndConditionsContent>
                        <TermsAndConditionsContent>
                          Refunds will only be accepted for products with valid
                          indications or if as advertised.
                        </TermsAndConditionsContent>
                        <TermsAndConditionsContent>
                          Refunds will be issued within 14 working days as
                          follows:
                        </TermsAndConditionsContent>
                        <TermsAndConditionsContent>
                          <UnorderedList w={'100%'} pl={4}>
                            <ListItem>
                              Credit card payment will be refunded through the
                              credit card
                            </ListItem>
                            <ListItem>
                              Paypal payment will be refunded through Paypal
                            </ListItem>
                            <ListItem>
                              Cash payment will be refunded through bank
                              transfer/cheque
                            </ListItem>
                          </UnorderedList>
                        </TermsAndConditionsContent>
                      </TermsAndConditionsContent>
                    </VStack>
                  </TermsAndConditionsContent>
                </VStack>
              </TermsAndConditionsContent>
              <TermsAndConditionsContent title="Instalment Plan Refund">
                <VStack spacing={6}>
                  <TermsAndConditionsContent>
                    Customers who have purchased through JML’s instalment plan
                    will have to wait up to 2 - 3 weeks before the bank approves
                    the credit back to the purchaser’s credit card. A refund
                    form will be signed when the customer visits the JML service
                    centre for the refund.
                  </TermsAndConditionsContent>
                  <TermsAndConditionsContent>
                    Customers who have purchased through JML’s instalment plan
                    will have to wait up to 2 - 3 weeks before the bank approves
                    the credit back to the purchaser’s credit card. A refund
                    form will be signed when the customer visits the JML service
                    centre for the refund.
                  </TermsAndConditionsContent>
                  <TermsAndConditionsContent>
                    JML Singapore Pte Ltd reserves the right to refuse any
                    exchange/refund/return should there be any shortages of
                    parts or packaging deemed non-conforming to our
                    specifications.
                  </TermsAndConditionsContent>
                </VStack>
              </TermsAndConditionsContent>
            </VStack>
          </TermsAndConditionsContent>

          <TermsAndConditionsContent title="3.3 Liability">
            If you have let us know of a problem with the goods, we will either
            make good any shortage or non-delivery and replace any damaged or
            defective goods upon delivery. This does not affect your statutory
            rights as a consumer, nor is it intended to limit our liability to
            you for fraudulent misrepresentation, death, or personal injury
            resulting from our negligence.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent
            title="3.4 Product Warranty"
            id={RouterUrl.ANCHOR.WARRANTY}
          >
            <VStack spacing={10}>
              <TermsAndConditionsContent>
                <TermsAndConditionsContent>
                  JML Singapore Pte Ltd provides a product warranty for a period
                  of twelve (12) months from the date of purchase, covering
                  defects in workmanship and materials. The warranty is
                  non-transferable and valid only in Singapore.
                </TermsAndConditionsContent>
                <UnorderedList pl={5}>
                  <ListItem>
                    Product warranty only covers electrical products with an
                    electrical plug (Excluding USB-powered/charged products).
                  </ListItem>
                  <ListItem>
                    For a period of twelve (12) months from the date of
                    purchase, JML Singapore Pte Ltd will supply new or rebuilt,
                    at its discretion, replacement parts in exchange for
                    defective parts at no cost to the purchaser. The warranty is
                    made to the purchaser only. All defective parts replaced
                    under this warranty will become the property of JML
                    Singapore Pte Ltd. In the event that part(s) or unit
                    replacement is not available, JML Singapore Pte Ltd will, at
                    its discretion, replace the original product with one of a
                    similar type of equal value. The warranty will be voided if
                    the manufacturer's instructions have yet to be followed or
                    if the product has been subject to misuse or any
                    modifications.
                  </ListItem>
                  <ListItem>
                    Verification of this warranty is undertaken by checking the
                    purchaser's original purchase records (purchase
                    receipt/invoice) before any remedial work is undertaken.
                    Failure of verification will result in the rejection of the
                    warranty service. In this instance, the product, at JML
                    Singapore Pte Ltd's sole discretion, will be returned to the
                    said purchaser.
                  </ListItem>
                  <ListItem>
                    Product warranty does not cover "hygiene", consumables,
                    perishables, USB-powered/charged products, any product that
                    has been opened/unsealed or used, or products on clearance
                    promotion.
                  </ListItem>
                </UnorderedList>
              </TermsAndConditionsContent>
              <TermsAndConditionsContent>
                This warranty does not affect your statutory rights.
              </TermsAndConditionsContent>
            </VStack>
          </TermsAndConditionsContent>
        </VStack>
      </VStack>

      {/* PRODUCT DISPLAY/ COLORS */}
      <TermsAndConditionsContent title="PRODUCT DISPLAY/ COLORS">
        Webstore attempts to display product images on the site as accurately as
        possible. However, we cannot guarantee that the colour you see matches
        the product colour, as the display of the colour depends, in part, upon
        the monitor you are using.
      </TermsAndConditionsContent>

      {/* SAFETY ADVICE */}
      <TermsAndConditionsContent title="SAFETY ADVICE">
        For your safety, we recommend that you read carefully all safety
        information printed either on the packaging or in any leaflets that may
        be included with the product. For electrical products, always check the
        power cable and plug for any signs of damage. Please always consult a
        qualified electrician and never try to repair any product yourself.
      </TermsAndConditionsContent>

      {/* DATA PROTECTION ACT 1998 */}
      <TermsAndConditionsContent
        title="DATA PROTECTION ACT 1998"
        id={RouterUrl.ANCHOR.PRIVACY_POLICY}
      >
        <TermsAndConditionsContent>
          JML is registered as a data user under the Data Protection Act 1998.
          The company's registered entry contains descriptions of the sources,
          uses, and disclosure of personal data held by the company. Information
          supplied by you will be used for administering your account details
          and for promoting goods and services to you. We may disclose your
          information to a third-party service provider for delivery.
        </TermsAndConditionsContent>
        <TermsAndConditionsContent>
          By purchasing products from us, you agree to these terms of sale. JML
          Singapore Pte Ltd has the right to revise these terms and conditions,
          including prices, at any time. We also have the right to decline
          service to any individual customer.
        </TermsAndConditionsContent>
      </TermsAndConditionsContent>

      {/* DISCLAIMERS */}
      <TermsAndConditionsContent title="DISCLAIMERS">
        <VStack spacing={5}>
          <TermsAndConditionsContent>
            By using the Webstore, you expressly agree that the use of the
            Webstore is at your sole risk. The Webstore is provided on an "as
            available" basis. Neither JML Singapore Pte Ltd nor affiliates,
            subsidiaries, designees, nor each of their respective officers,
            directors, employees, agents, third-party content providers,
            distributors, or merchants warrant that use of the Webstore will be
            uninterrupted or error-free.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent>
            Under no circumstances shall JML Singapore Pte Ltd or its Associates
            be liable for any direct, indirect, incidental, special, or
            consequential damages that result from your use of or inability to
            use the Webstore, including but not limited to reliance by you on
            any information obtained from the Webstore that results in mistakes,
            omissions, interruptions, deletion, or corruption of files, viruses,
            delays in operation or transmission, or any failure of performance.
            The foregoing Limitation of Liability shall apply in any action,
            whether in contract, tort, or any other claim, even if an authorized
            representative of JML Singapore Pte Ltd has been advised of or
            should have knowledge of the possibility of such damages. User
            hereby acknowledges that this paragraph shall apply to all content,
            merchandise, and services available through the Webstore.
          </TermsAndConditionsContent>
        </VStack>
      </TermsAndConditionsContent>

      {/* ENTIRE AGREEMENT */}
      <TermsAndConditionsContent title="ENTIRE AGREEMENT">
        These terms and conditions, together with your sales order, are the
        entire agreement between you and JML Singapore Pte Ltd concerning your
        purchase of the Goods and supersede any and all statements or other
        agreements.
      </TermsAndConditionsContent>

      {/* TERMINATION OF USE */}
      <TermsAndConditionsContent title="TERMINATION OF USE">
        JML Singapore Pte Ltd may, in its sole discretion, terminate your
        account or your use of the Webstore at any time. You are personally
        liable for any orders that you place or charges that you incur prior to
        termination. JML Singapore Pte Ltd reserves the right to change,
        suspend, or discontinue all or any aspects of the Webstore at any time
        without prior notice.
      </TermsAndConditionsContent>

      {/* FEEDBACKS, COMMENTS, SUGGESTIONS */}
      <TermsAndConditionsContent title="FEEDBACKS, COMMENTS, SUGGESTIONS">
        <VStack spacing={5}>
          <TermsAndConditionsContent>
            Visitors may post feedback, comments, and submit suggestions, ideas,
            comments, questions, or other information, so long as the content is
            not illegal, obscene, threatening, defamatory, invasive of privacy,
            infringing of intellectual property rights, or otherwise injurious
            to third parties or objectionable and does not consist of or contain
            software viruses, political campaigning, commercial solicitation,
            chain letters, mass mailings, or any form of "spam." You may not use
            a false e-mail address, impersonate any person or entity, or
            otherwise mislead as to the origin of a credit card or any other
            matter. JML Singapore Pte Ltd reserves the right (but not the
            obligation) to remove or edit such content but does not regularly
            review posted content.
          </TermsAndConditionsContent>
          <TermsAndConditionsContent>
            If you do post content or submit material, and unless we indicate
            otherwise, you grant JML Singapore Pte Ltd a perpetual, irrevocable,
            and fully sub-licensable right to use, reproduce, modify, adapt,
            publish, translate, create derivative works from, distribute, and
            display such content throughout the world in any media, the right to
            use the name that you submit in connection with such content. You
            represent and warrant that you own or otherwise control all of the
            rights to the content that you post; that the content is accurate;
            that use of the content you supply does not violate this policy and
            will not cause injury to any person or entity; and that you will
            indemnify JML Singapore Pte Ltd for all claims resulting (whether
            directly or indirectly) from content you supply. JML Singapore Pte
            Ltd has the right but not the obligation to monitor and edit or
            remove any activity or content. JML Singapore Pte Ltd takes no
            responsibility and assumes no liability for any content posted by
            you or any third party
          </TermsAndConditionsContent>
        </VStack>
      </TermsAndConditionsContent>
    </FooterInfoLayout>
  )
}

export default TermsAndConditions
